/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type {
  AuthConfigurationV3,
  DomainToken,
  LicensePlateResponseV2,
  ProvisionDevicesResponseV2
} from "@tivocorp/data-domain-lib";

import {
  buildClaimJwt,
  buildPrimaryCredentialJwt,
  extractServiceErrorMessage,
  localizedErrorFallback
} from "../../../model/utility";
import { LocaleKeys } from "../../../model/locale";
import { Log } from "../../../model/logging";
import {
  SignInFlow,
  SignInFlowEvent,
  SignInLogger,
  SignInStep,
  type SignInStepName
} from "../../../model/logging/SignInLogger";
import { SignInInteractor } from "../../../model/interactor";
import { DeviceModel } from "../../../model/device";
import { TokenManager } from "../../../model/auth";
import { RetryMechanism } from "../../../model/retrymechanism";
import { DiagnosticSeverity } from "../../../model/types";
import type { UnityRootState } from "../../UnityAppStore";
import type { TAuthorizationSchemes } from "../common";
import {
  beginDbsPollingSession,
  beginSignInAttempt,
  cancelSignInAttempt,
  completeSignInAttempt,
  createCriticalRetryOptions,
  extractHttpStatus,
  extractSignInErrorCode,
  failSignInAttempt,
  markStep,
  markStepReached,
  persistProvisionData,
  resolveDbsSchemes,
  scheduleNextDbsPoll,
  SIGN_IN_FALLBACK_ERROR_CODE
} from "../common";
import {
  DBS_ERROR_LP_EXPIRED,
  DBS_ERROR_POLLING_CANCELLED,
  HTTP_STATUS_NOT_FOUND
} from "../signIn.constants";

const TAG: string = "signIn/dbsLicensePlateThunks";

/** Diagnostic text, not user copy — goes to SignInLogger, never to a screen. */
const FLOW_FAILED_MESSAGE: string = "DBS license plate flow failed";
const ERROR_CODE_NO_CONFIG: string = "NO_DBS_CONFIG";
/**
 * License-plate expiry has no catalog code for this operation. `A304` names
 * the condition ('EXPIRED_LICENSEPLATE') but on `POST /v1/samlToken`, which
 * unity-web does not call — emitting it for a `dbsJwtToken` failure would be
 * the "near-enough" code IMPLEMENTATION_GUIDANCE.md §6 forbids. Reports the
 * catch-all until `dbsJwtToken` is allocated upstream; the exact condition
 * stays in `reason` and in the logLicensePlateExpired diagnostic.
 */
const ERROR_CODE_LP_EXPIRED: string = SIGN_IN_FALLBACK_ERROR_CODE;
const LP_EXPIRED_REASON: string = "LICENSE_PLATE_EXPIRED";

/** Reason recorded when polling is cancelled before the user binds. */
const CANCEL_REASON_POLLING_CANCELLED: string = "polling_cancelled";

/** `outcome` values on dbs_poll_attempt diagnostics. */
const POLL_OUTCOME_BOUND: string = "bound";
const POLL_OUTCOME_NOT_BOUND: string = "not_bound";
const POLL_OUTCOME_ERROR: string = "error";

/**
 * DBS License Plate Binding — Full first-time flow.
 * Orchestrates: claim → DBS token → license plate → sets UI ready for polling.
 *
 * Each step uses CRITICAL retry — the overlay is shown on failure and the
 * thunk blocks until each endpoint resolves.
 */
export const startDbsLicensePlateFlow = createAsyncThunk<
  {
    dbsToken: string;
    dbsTokenExpiration: number;
    licensePlate: LicensePlateResponseV2;
    licensePlateBindingUrl: string;
    licensePlateBindingCredentialUrl: string;
  },
  { signal: AbortSignal },
  { state: UnityRootState; rejectValue: string }
>(
  "signIn/startDbsLicensePlateFlow",
  async (
    { signal }: { signal: AbortSignal },
    { getState, dispatch, rejectWithValue }
  ) => {
    const flowStartTime: number = Date.now();
    let stepReached: string = SignInStep.Config;
    try {
      Log.d(TAG, "startDbsLicensePlateFlow() starting DBS flow");
      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowStarted,
        "DBS license plate flow started",
        DiagnosticSeverity.Info,
        { flow: SignInFlow.Dbs }
      );
      const signInInteractor: SignInInteractor = new SignInInteractor();
      const state: UnityRootState = getState();
      const msoName: string = DeviceModel.getTiVoSettings().getMsoName();

      // Look up DBS config from authConfigurations
      const dbsConfig: AuthConfigurationV3 | undefined =
        state.signIn.authConfigurations.find(
          (c: AuthConfigurationV3): boolean =>
            c.authenticationType === AuthenticationTypeV3.DbsLicensePlateBinding
        );

      if (dbsConfig === undefined) {
        SignInLogger.logFlowEvent(
          SignInFlowEvent.FlowFailed,
          FLOW_FAILED_MESSAGE,
          DiagnosticSeverity.Error,
          {
            flow: SignInFlow.Dbs,
            step_reached: SignInStep.Config,
            error_code: ERROR_CODE_NO_CONFIG
          }
        );
        return rejectWithValue(
          "No dbsLicensePlateBinding auth configuration found"
        );
      }

      const lastAuthContext = await DeviceModel.getLastAuthContext();
      beginSignInAttempt({
        flow: SignInFlow.Dbs,
        authType: AuthenticationTypeV3.DbsLicensePlateBinding,
        isFirstSignIn:
          lastAuthContext === undefined || lastAuthContext === null,
        // The user opens the code screen and binds on their phone.
        isUserInitiated: true,
        msoName: msoName,
        attemptedTypes: state.signIn.attemptedAuthTypes
      });

      // Step 2: Claim — self-sign the device public key with the device key (CRITICAL retry)
      stepReached = SignInStep.Claim;
      markStepReached(SignInStep.Claim);
      Log.d(TAG, "startDbsLicensePlateFlow() Step 2: claiming device");
      const claimScheme: string = dbsConfig.authorizationSchemes.claim;
      const claimJwt: string = await buildClaimJwt();
      const claimStartedAt: number = Date.now();
      await RetryMechanism.callServiceWithRetry(
        () =>
          signInInteractor.fetchClaim({
            authorization: claimScheme + " " + claimJwt
          }),
        [],
        createCriticalRetryOptions(dispatch),
        signal
      );
      markStep(SignInStep.Claim, Date.now() - claimStartedAt);

      // Step 3: Get DBS-specific token (CRITICAL retry)
      stepReached = SignInStep.DomainToken;
      markStepReached(SignInStep.DomainToken);
      Log.d(
        TAG,
        "startDbsLicensePlateFlow() Step 3: fetching DBS-specific token"
      );
      const domainTokenScheme: string =
        dbsConfig.authorizationSchemes.domainToken;
      const primaryCredential: string = await buildPrimaryCredentialJwt();
      const dbsTokenStartedAt: number = Date.now();
      const dbsTokenResponse: DomainToken =
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.fetchDomainToken({
              authorization: domainTokenScheme + " " + primaryCredential
            }),
          [],
          createCriticalRetryOptions(dispatch),
          signal
        );
      markStep(SignInStep.DomainToken, Date.now() - dbsTokenStartedAt);
      const dbsToken: string = dbsTokenResponse.token;
      const dbsTokenExpiration: number = dbsTokenResponse.expiration;

      // Step 4: Generate license plate (CRITICAL retry)
      stepReached = SignInStep.LicensePlate;
      markStepReached(SignInStep.LicensePlate);
      const licensePlateStartedAt: number = Date.now();
      Log.d(TAG, "startDbsLicensePlateFlow() Step 4: generating license plate");
      const licensePlate: LicensePlateResponseV2 =
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.createLicensePlateV2(
              { msoName, newLicensePlate: true },
              dbsToken
            ),
          [],
          createCriticalRetryOptions(dispatch),
          signal
        );

      markStep(SignInStep.LicensePlate, Date.now() - licensePlateStartedAt);

      // Extract dbsMetaData URLs
      const licensePlateBindingUrl: string =
        dbsConfig.dbsMetaData !== undefined && dbsConfig.dbsMetaData !== null
          ? dbsConfig.dbsMetaData.licensePlateBindingUrl || ""
          : "";
      const licensePlateBindingCredentialUrl: string =
        dbsConfig.dbsMetaData !== undefined && dbsConfig.dbsMetaData !== null
          ? dbsConfig.dbsMetaData.licensePlateBindingCredentialUrl || ""
          : "";

      // The license plate code is a live binding secret — never log its value.
      Log.d(TAG, "startDbsLicensePlateFlow() license plate generated");

      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowCompleted,
        "DBS license plate issued",
        DiagnosticSeverity.Info,
        {
          flow: SignInFlow.Dbs,
          step_reached: stepReached,
          polling_interval_sec: String(licensePlate.pollingIntervalSeconds),
          duration_ms: String(Date.now() - flowStartTime)
        }
      );
      SignInLogger.logLicensePlateIssued({
        flow: SignInFlow.Dbs,
        polling_interval_sec: String(licensePlate.pollingIntervalSeconds),
        duration_ms: String(Date.now() - flowStartTime)
      });

      return {
        dbsToken,
        dbsTokenExpiration,
        licensePlate,
        licensePlateBindingUrl,
        licensePlateBindingCredentialUrl
      };
    } catch (error: unknown) {
      SignInLogger.logFlowFailure(
        SignInFlowEvent.FlowFailed,
        FLOW_FAILED_MESSAGE,
        error,
        {
          flow: SignInFlow.Dbs,
          step_reached: stepReached,
          duration_ms: String(Date.now() - flowStartTime)
        }
      );
      failSignInAttempt(
        extractSignInErrorCode(error),
        stepReached as SignInStepName
      );
      Log.e(TAG, "startDbsLicensePlateFlow()", error);
      return rejectWithValue(
        extractServiceErrorMessage(
          error,
          localizedErrorFallback(LocaleKeys.SIGN_IN_CODE_FLOW_ERROR_MESSAGE)
        )
      );
    }
  }
);

interface DbsPollAttemptParams {
  signInInteractor: SignInInteractor;
  msoName: string;
  dlid: string;
  licensePlateCode: string;
  licensePlateCredential: string;
  dbsToken: string;
  signal: AbortSignal;
  expirationMs: number;
  pollingIntervalMs: number;
}

/**
 * Executes a single DBS polling attempt. Returns the JWT token string
 * on successful binding, or rejects with an error.
 *
 * Retries on non-404 server errors at the polling interval.
 */
function dbsPollAttempt(params: DbsPollAttemptParams): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const pollStartTime: number = Date.now();
    let attemptCount: number = 0;

    const attempt = (): void => {
      attemptCount = attemptCount + 1;

      if (params.signal.aborted) {
        SignInLogger.logFlowEvent(
          SignInFlowEvent.DbsPollCancelled,
          "DBS binding poll cancelled",
          DiagnosticSeverity.Info,
          {
            attempt_count: String(attemptCount),
            elapsed_ms: String(Date.now() - pollStartTime)
          }
        );
        cancelSignInAttempt(CANCEL_REASON_POLLING_CANCELLED);
        reject(new Error(DBS_ERROR_POLLING_CANCELLED));
        return;
      }

      const now: number = Date.now();
      if (now >= params.expirationMs) {
        SignInLogger.logFlowEvent(
          SignInFlowEvent.DbsPollExpired,
          "DBS license plate expired before binding",
          DiagnosticSeverity.Warning,
          {
            attempt_count: String(attemptCount),
            elapsed_ms: String(now - pollStartTime)
          }
        );
        SignInLogger.logLicensePlateExpired({
          flow: SignInFlow.Dbs,
          reason: LP_EXPIRED_REASON,
          attempt_count: String(attemptCount),
          elapsed_ms: String(now - pollStartTime)
        });
        // The attempt opened in startDbsLicensePlateFlow() ends here — the
        // user never completed the bind, so completeDbsBinding() will not run
        // and nothing else would close the transaction.
        failSignInAttempt(ERROR_CODE_LP_EXPIRED, SignInStep.DbsJwtToken);
        reject(new Error(DBS_ERROR_LP_EXPIRED));
        return;
      }

      params.signInInteractor
        .createDbsJwtToken(
          {
            msoName: params.msoName,
            deviceLoggingId: params.dlid,
            licensePlateData: {
              licensePlateCode: params.licensePlateCode,
              licensePlateCredential: params.licensePlateCredential
            }
          },
          params.dbsToken,
          params.signal
        )
        .then((response) => {
          SignInLogger.logDbsPollAttempt(attemptCount, true, {
            outcome: POLL_OUTCOME_BOUND,
            elapsed_ms: String(Date.now() - pollStartTime)
          });
          resolve(response.token);
        })
        .catch((pollError: unknown) => {
          if (params.signal.aborted) {
            SignInLogger.logFlowEvent(
              SignInFlowEvent.DbsPollCancelled,
              "DBS binding poll cancelled",
              DiagnosticSeverity.Info,
              {
                attempt_count: String(attemptCount),
                elapsed_ms: String(Date.now() - pollStartTime)
              }
            );
            cancelSignInAttempt(CANCEL_REASON_POLLING_CANCELLED);
            reject(new Error(DBS_ERROR_POLLING_CANCELLED));
            return;
          }

          const httpStatus: number | null = extractHttpStatus(pollError);
          const isNotFound: boolean = httpStatus === HTTP_STATUS_NOT_FOUND;

          // Sampled — a license plate lives for minutes and polls every few
          // seconds, so logging every tick would evict earlier pending logs.
          SignInLogger.logDbsPollAttempt(attemptCount, false, {
            outcome: isNotFound ? POLL_OUTCOME_NOT_BOUND : POLL_OUTCOME_ERROR,
            http_status: httpStatus === null ? undefined : String(httpStatus),
            elapsed_ms: String(Date.now() - pollStartTime)
          });

          if (isNotFound) {
            Log.d(
              TAG,
              "startDbsPolling() not yet bound, retrying in " +
                String(params.pollingIntervalMs) +
                "ms"
            );
          } else {
            Log.d(
              TAG,
              "startDbsPolling() server error, retrying in " +
                String(params.pollingIntervalMs) +
                "ms"
            );
          }

          scheduleNextDbsPoll(attempt, params.pollingIntervalMs);
        });
    };
    attempt();
  });
}

/**
 * DBS Polling — Polls v1/dbsJwtToken at the configured interval until
 * the user binds or the license plate expires.
 */
export const startDbsPolling = createAsyncThunk<
  string,
  void,
  { state: UnityRootState; rejectValue: string }
>("signIn/startDbsPolling", async (_: void, { getState, rejectWithValue }) => {
  try {
    Log.d(TAG, "startDbsPolling() initiating polling loop");

    // Cancel any existing polling and take the new session's signal
    const signal: AbortSignal = beginDbsPollingSession();

    const signInInteractor: SignInInteractor = new SignInInteractor();
    const state: UnityRootState = getState();
    const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
    const dlid: string = DeviceModel.getDeviceLoggingId();
    const dbsToken: string = state.signIn.dbsToken;
    const licensePlateCode: string = state.signIn.licensePlateCode;
    const licensePlateCredential: string = state.signIn.licensePlateCredential;
    const pollingIntervalMs: number =
      state.signIn.pollingIntervalSeconds * 1000;
    const expirationTime: string = state.signIn.licensePlateExpirationTime;
    const expirationMs: number = new Date(expirationTime).getTime();

    const jwtToken: string = await dbsPollAttempt({
      signInInteractor,
      msoName,
      dlid,
      licensePlateCode,
      licensePlateCredential,
      dbsToken,
      signal,
      expirationMs,
      pollingIntervalMs
    });
    Log.d(TAG, "startDbsPolling() DBS JWT token received");
    return jwtToken;
  } catch (error: unknown) {
    Log.e(TAG, "startDbsPolling()", error);
    return rejectWithValue(
      extractServiceErrorMessage(
        error,
        localizedErrorFallback(LocaleKeys.SIGN_IN_CODE_POLLING_ERROR_MESSAGE)
      )
    );
  }
});

/**
 * DBS Bind and Complete — After polling succeeds:
 * bind → domainToken → provisionDevices
 *
 * Each step uses CRITICAL retry — the overlay is shown on failure and the
 * thunk blocks until each endpoint resolves.
 */
export const completeDbsBinding = createAsyncThunk<
  { domainToken: DomainToken; provisionResponse: ProvisionDevicesResponseV2 },
  { signal: AbortSignal },
  { state: UnityRootState; rejectValue: string }
>(
  "signIn/completeDbsBinding",
  async (
    { signal }: { signal: AbortSignal },
    { getState, dispatch, rejectWithValue }
  ) => {
    const flowStartTime: number = Date.now();
    let stepReached: string = SignInStep.Bind;
    try {
      Log.d(TAG, "completeDbsBinding() starting bind and provision");
      const signInInteractor: SignInInteractor = new SignInInteractor();
      const state: UnityRootState = getState();
      const dbsJwtToken: string = state.signIn.dbsJwtToken;
      const primaryCredential: string = await buildPrimaryCredentialJwt();
      const dlid: string = DeviceModel.getDeviceLoggingId();

      // Look up DBS config for authorization schemes
      const dbsConfig: AuthConfigurationV3 | undefined =
        state.signIn.authConfigurations.find(
          (c: AuthConfigurationV3): boolean =>
            c.authenticationType === AuthenticationTypeV3.DbsLicensePlateBinding
        );
      const dbsSchemes: TAuthorizationSchemes =
        dbsConfig === undefined
          ? await resolveDbsSchemes()
          : dbsConfig.authorizationSchemes;
      const bindScheme: string = dbsSchemes.bind;
      const domainTokenScheme: string = dbsSchemes.domainToken;

      // Step 7: Bind (CRITICAL retry)
      markStepReached(SignInStep.Bind);
      Log.d(TAG, "completeDbsBinding() Step 7: binding device");
      const bindStartedAt: number = Date.now();
      await RetryMechanism.callServiceWithRetry(
        () =>
          signInInteractor.fetchBind({
            authorization:
              bindScheme + " " + primaryCredential + " " + dbsJwtToken
          }),
        [],
        createCriticalRetryOptions(dispatch),
        signal
      );
      markStep(SignInStep.Bind, Date.now() - bindStartedAt);

      // Step 8: Get final domain token (CRITICAL retry)
      stepReached = SignInStep.DomainToken;
      markStepReached(SignInStep.DomainToken);
      const finalTokenStartedAt: number = Date.now();
      Log.d(TAG, "completeDbsBinding() Step 8: fetching domain token");
      const domainToken: DomainToken =
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.fetchDomainToken({
              authorization: domainTokenScheme + " " + primaryCredential
            }),
          [],
          createCriticalRetryOptions(dispatch),
          signal
        );

      markStep(SignInStep.DomainToken, Date.now() - finalTokenStartedAt);

      // Hand the token to its owner: TokenManager caches it and persists it
      // (no storage expiry; refresh is driven by the token's own expiration).
      await TokenManager.adoptToken(domainToken, null);

      // Step 9: Provision devices (CRITICAL retry)
      stepReached = SignInStep.ProvisionDevices;
      markStepReached(SignInStep.ProvisionDevices);
      const provisionStartedAt: number = Date.now();
      Log.d(TAG, "completeDbsBinding() Step 9: provisioning devices");
      const provisionResponse: ProvisionDevicesResponseV2 =
        await RetryMechanism.callServiceWithRetry(
          () =>
            signInInteractor.createProvisionDevicesV2(
              {
                provisionDevicesRequestV2: {
                  authenticationType: "dbsLicensePlateBinding",
                  deviceLoggingId: dlid,
                  deviceConfig: {}
                }
              },
              domainToken.token
            ),
          [],
          createCriticalRetryOptions(dispatch),
          signal
        );

      markStep(SignInStep.ProvisionDevices, Date.now() - provisionStartedAt);

      await persistProvisionData(provisionResponse);

      // Persist the auth context so a later token refresh uses the schemes the
      // auth-config service advertised for this flow instead of defaults.
      await DeviceModel.setLastAuthContext({
        authenticationType: AuthenticationTypeV3.DbsLicensePlateBinding,
        authorizationSchemes: {
          claim: dbsSchemes.claim,
          bind: bindScheme,
          domainToken: domainTokenScheme
        }
      });

      // Sign-in always lands in the account context; a profile is chosen later.
      await TokenManager.setActiveContext(null);
      Log.d(TAG, "completeDbsBinding() DBS binding completed successfully");

      SignInLogger.logFlowEvent(
        SignInFlowEvent.FlowCompleted,
        "DBS license plate flow completed",
        DiagnosticSeverity.Info,
        {
          flow: SignInFlow.Dbs,
          auth_type: String(AuthenticationTypeV3.DbsLicensePlateBinding),
          duration_ms: String(Date.now() - flowStartTime)
        }
      );
      completeSignInAttempt();
      return { domainToken, provisionResponse };
    } catch (error: unknown) {
      SignInLogger.logFlowFailure(
        SignInFlowEvent.FlowFailed,
        FLOW_FAILED_MESSAGE,
        error,
        {
          flow: SignInFlow.Dbs,
          step_reached: stepReached,
          duration_ms: String(Date.now() - flowStartTime)
        }
      );
      failSignInAttempt(
        extractSignInErrorCode(error),
        stepReached as SignInStepName
      );
      Log.e(TAG, "completeDbsBinding()", error);
      return rejectWithValue(
        extractServiceErrorMessage(
          error,
          localizedErrorFallback(
            LocaleKeys.SIGN_IN_CODE_ACTIVATION_ERROR_MESSAGE
          )
        )
      );
    }
  }
);
